import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import seller profile components (lazy-loaded for performance)
const SellerProfilesList = React.lazy(() => import("./SellerProfilesList"));
const NewSellerProfile = React.lazy(() => import("./NewSellerProfile"));
const SellerProfileView = React.lazy(() => import("./SellerProfileView"));

/**
 * Seller Profile Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (sellerprofile/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/sellerprofile/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const sellerProfileModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "sellerprofile",
    name: "Seller Profile Management",
    description: "Manage seller profiles and vendor information",
    version: "1.0.0",
    enabled: true,
    category: "marketplace",
    order: 5,
    icon: "🏪",
    color: "#805AD5",

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/seller-profiles",
            component: SellerProfilesList,
            permissionLevel: "view",
        },
        {
            path: "/seller-profiles/new",
            component: NewSellerProfile,
            permissionLevel: "create",
        },
        {
            path: "/seller-profile/:id",
            component: SellerProfileView,
            permissionLevel: "view",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Seller Profiles",
            path: "/seller-profiles",
            icon: "🏪",
            permissionLevel: "view",
        },
        {
            label: "New Seller Profile",
            path: "/seller-profiles/new",
            icon: "➕",
            permissionLevel: "create",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/sellerprofile/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Add Seller Profile",
            path: "/seller-profiles/new",
            icon: "➕",
            description: "Create a new seller profile"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "Production-ready seller profile management",
                "Complete vendor information system",
                "IPFS integration for seller media",
                "Full multi-seller marketplace support",
                "Life Essential Club integration",
                "Theme system with light/dark mode"
            ],
            notes: "Official stable release with complete marketplace functionality."
        },
        {
            version: "0.8.0",
            releaseDate: "2024-10-31",
            title: "IPFS Media Integration",
            features: [
                "Integrated IPFS server for seller profile images",
                "Decentralized media storage for seller assets",
                "Improved image handling and persistence"
            ],
            notes: "Enhanced with IPFS integration for decentralized seller media storage."
        },
        {
            version: "0.7.0",
            releaseDate: "2024-09-20",
            title: "Theme System Implementation",
            features: [
                "Full light/dark mode support",
                "Theme-aware styling across all seller profile components",
                "Enhanced visual consistency",
                "Theme migration documentation"
            ],
            notes: "Major UI overhaul with comprehensive theme system."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-07-14",
            title: "Module Configuration System",
            features: [
                "Created moduleConfig.ts structure (75 lines)",
                "Defined routes and navigation",
                "Configured permission system",
                "Added quick actions",
                "Integrated with profile management"
            ],
            notes: "Established formal module configuration architecture."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-07-13",
            title: "Authentication System Integration",
            features: [
                "Integrated centralized authentication module",
                "Updated NewSellerProfile authentication",
                "Improved auth component imports"
            ],
            notes: "Enhanced security with centralized authentication."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-07-01",
            title: "Life Essential Club Features",
            features: [
                "Added Life Essential Club integration",
                "Enhanced NewSellerProfile with club features (66 lines modified)",
                "Simplified SellerProfileView UI (81 lines modified)",
                "Club-specific seller categorization"
            ],
            notes: "Integration with Life Essential Club marketplace features."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-07-01",
            title: "GraphQL Query Optimization",
            features: [
                "Refactored seller profiles GraphQL query",
                "Improved data handling in SellerProfilesList",
                "Better query performance",
                "Enhanced data structure"
            ],
            notes: "Backend query optimization for better performance."
        },
        {
            version: "0.2.0",
            releaseDate: "2024-06-30",
            title: "Feature Enhancement & Expansion",
            features: [
                "Massive SellerProfileView enhancement (715 lines added)",
                "Enhanced NewSellerProfile functionality",
                "Advanced seller profile display",
                "Improved seller management features",
                "Enhanced navigation integration"
            ],
            notes: "Major feature expansion with comprehensive seller profile functionality."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-06-29",
            title: "Initial Seller Profile System",
            features: [
                "Created NewSellerProfile component (917 lines)",
                "Created SellerProfileView component (747 lines)",
                "Created SellerProfilesList component (566 lines)",
                "Complete seller profile CRUD operations",
                "Seller information management",
                "Vendor profile display",
                "Multi-seller marketplace foundation"
            ],
            notes: "Initial implementation with complete seller profile management (2,230 lines of code)."
        }
    ],

    // ROADMAP
    roadmap: []

};

export default sellerProfileModuleConfig;
